import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query"
import { ArrowLeft, ArrowUpRight, FileText, RefreshCw } from "lucide-react"
import { useState } from "react"
import { Link, useParams, useSearchParams } from "react-router"
import { CompanyAutomation } from "./CompanyAutomation"
import { CurationBadge, CurationPanel } from "./CurationPanel"
import { DiscoveryButton } from "./DiscoveryButton"
import { DiscoveryReviewPanel } from "./DiscoveryReviewPanel"
import { ManualDocumentUpload } from "./ManualDocumentUpload"
import { PilotRequestPanel } from "./PilotRequestPanel"
import { RecheckButton } from "./RecheckButton"
import { catalog, curation, INDEX_PATH } from "./api"

export function CompanyProfilePage() {
  const { companyId = "" } = useParams()
  const [params] = useSearchParams()
  const requestedDocument = params.get("document")
  const flag = useFeatureFlag("COMPANY_INDEX")
  const handoff = useFeatureFlag("COMPANY_INDEX_ARTICLE_HANDOFF")
  const { user, hasPermission } = useUser()
  const enabled = flag && !!companyId && !!user?.organization
  const { translate: t } = useTranslation()
  const [search, setSearch] = useState("")
  const [selection, setSelection] = useState({
    request: requestedDocument,
    id: requestedDocument,
  })
  const expanded =
    selection.request === requestedDocument ? selection.id : requestedDocument
  const setExpanded = (id: string | null) =>
    setSelection({ request: requestedDocument, id })
  const [filter, setFilter] = useState("all")
  const attentionOnly = filter === "attention"
  const [view, setView] = useState<"review" | "sources" | "history">("review")
  const company = useQuery({
    queryKey: ["company-index", user?.organization, "company", companyId],
    queryFn: () => catalog.company(companyId),
    enabled,
  })
  const documents = useInfiniteQuery({
    queryKey: [
      "company-index",
      user?.organization,
      "documents",
      companyId,
      attentionOnly,
    ],
    queryFn: ({ pageParam }) =>
      attentionOnly
        ? curation.attention(companyId, pageParam)
        : catalog.documents(companyId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
    refetchInterval: 10000,
  })
  const selectedDocument = useQuery({
    queryKey: [
      "company-index",
      user?.organization,
      "document",
      requestedDocument,
    ],
    queryFn: () => catalog.document(requestedDocument!),
    enabled: enabled && !!requestedDocument,
    refetchInterval: 10000,
  })
  const download = useMutation({
    mutationFn: catalog.download,
    onSuccess: ({ url }) => {
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.rel = "noreferrer"
      anchor.target = "_blank"
      anchor.click()
    },
  })
  if (!flag) return null
  const loaded = documents.data?.pages.flatMap((page) => page.items) ?? []
  const selected =
    selectedDocument.data?.companyId === companyId
      ? selectedDocument.data
      : undefined
  const docs =
    selected &&
    (!attentionOnly ||
      selected.decision === "unreviewed" ||
      selected.needsAttention ||
      selected.overdue)
      ? [selected, ...loaded.filter((doc) => doc.id !== selected.id)]
      : loaded
  const visibleDocs = docs.filter(
    (doc) =>
      doc.filename.toLowerCase().includes(search.toLowerCase()) &&
      (filter === "all" ||
        filter === "attention" ||
        (filter === "source" && doc.needsAttention) ||
        (filter === "overdue" && doc.overdue) ||
        doc.decision === filter),
  )
  return (
    <div className="mx-auto space-y-7 max-w-7xl p-6 lg:p-10">
      <Link
        className="flex items-center gap-2 text-sm text-muted-foreground"
        to={INDEX_PATH}
      >
        <ArrowLeft className="size-4" />
        {t("CI_TITLE")}
      </Link>
      {company.isPending && <p>{t("CI_LOADING")}</p>}
      {company.isError && (
        <p role="alert">
          {t("CI_ERROR")}{" "}
          <Button onClick={() => void company.refetch()}>
            {t("CI_RETRY")}
          </Button>
        </p>
      )}
      {company.data && (
        <>
          <header>
            <p className="text-xs tracking-widest text-muted-foreground">
              {t("CI_WORKSPACE")}
            </p>
            <h1 className="my-3 text-3xl font-semibold">{company.data.name}</h1>
            <a
              className="inline-flex items-center gap-2 text-sm text-muted-foreground"
              href={company.data.website}
              target="_blank"
              rel="noreferrer"
            >
              {company.data.website}
              <ArrowUpRight className="size-4" />
            </a>
          </header>
          {hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD) && (
            <DiscoveryButton key={company.data.id} company={company.data} />
          )}
          {handoff && (
            <PilotRequestPanel
              key={`request-${companyId}`}
              companyId={companyId}
            />
          )}
          <ManualDocumentUpload
            key={`upload-${companyId}`}
            companyId={companyId}
          />
          <details className="rounded-xl border p-4">
            <summary className="cursor-pointer font-medium">
              {t("CI_AUTOMATION")}
            </summary>
            <CompanyAutomation
              key={company.data.id}
              companyId={company.data.id}
            />
          </details>
          <DiscoveryReviewPanel
            key={`discovery-review-${company.data.id}`}
            company={company.data}
          />
        </>
      )}
      <RecheckButton companyId={companyId} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">
            {t("CI_DOCUMENTS")} · {docs.length}
            {documents.hasNextPage ? "+" : ""}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("CI_PRIVATE")}
          </p>
        </div>
        <Button
          variant="outline"
          disabled={documents.isFetching}
          onClick={() => void documents.refetch()}
        >
          <RefreshCw className="size-4" />
          {t("CI_REFRESH")}
        </Button>
      </div>
      <Input
        aria-label={t("CI_DOCUMENT_SEARCH")}
        placeholder={t("CI_DOCUMENT_SEARCH")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <label className="flex flex-wrap items-center gap-3 text-sm">
        {t("CI_STATUS_FILTER")}
        <select
          aria-label={t("CI_STATUS_FILTER")}
          value={filter}
          className="rounded-md border border-border bg-background p-2"
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="all">{t("CI_ALL_DOCUMENTS")}</option>
          <option value="attention">{t("CI_NEEDS_REVIEW")}</option>
          <option value="unreviewed">{t("CI_UNREVIEWED_ONLY")}</option>
          <option value="source">{t("CI_SOURCE_NEEDS_REVIEW")}</option>
          <option value="overdue">{t("CI_CHECK_OVERDUE")}</option>
          <option value="approved">{t("CI_REVIEWED")}</option>
          <option value="rejected">{t("CI_REJECTED")}</option>
        </select>
      </label>
      {documents.hasNextPage && (filter !== "all" || !!search) && (
        <p className="text-sm text-muted-foreground">
          {t("CI_FILTER_LOADED_HINT")}
        </p>
      )}
      {selectedDocument.isError && (
        <p role="alert">
          {t("CI_ERROR")}{" "}
          <Button onClick={() => void selectedDocument.refetch()}>
            {t("CI_RETRY")}
          </Button>
        </p>
      )}
      {documents.isPending && <p>{t("CI_LOADING")}</p>}
      {documents.isError && (
        <p role="alert">
          {t("CI_ERROR")}{" "}
          <Button onClick={() => void documents.refetch()}>
            {t("CI_RETRY")}
          </Button>
        </p>
      )}
      {documents.isSuccess && !docs.length && (
        <p className="rounded-xl border border-dashed p-10 text-center text-muted-foreground">
          {t("CI_DOCUMENT_EMPTY")}
        </p>
      )}
      {download.isError && (
        <p role="alert" className="text-destructive">
          {t("CI_ERROR")}
        </p>
      )}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {visibleDocs.map((doc) => (
          <article
            key={doc.id}
            className="border-b border-border last:border-0"
          >
            <div className="flex flex-wrap items-center gap-4 p-5">
              <FileText className="size-7 shrink-0 text-muted-foreground" />
              <div className="min-w-48 flex-1">
                <h3 className="font-medium break-all">{doc.filename}</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  {doc.contentType === "application/pdf" ? "PDF" : "Workbook"} ·{" "}
                  {Math.ceil(doc.byteSize / 1024)} KB ·{" "}
                  {new Date(doc.createdAt).toLocaleDateString()}
                </p>
              </div>
              <CurationBadge document={doc} />
              <Button
                variant="outline"
                disabled={download.isPending}
                onClick={() => download.mutate(doc.id)}
              >
                {t("CI_DOWNLOAD")}
              </Button>
              {(["review", "sources", "history"] as const).map((section) => (
                <Button
                  key={section}
                  variant={
                    expanded === doc.id && view === section
                      ? "secondary"
                      : "ghost"
                  }
                  aria-expanded={expanded === doc.id && view === section}
                  onClick={() => {
                    setExpanded(
                      expanded === doc.id && view === section ? null : doc.id,
                    )
                    setView(section)
                  }}
                >
                  {t(
                    section === "review"
                      ? "CI_REVIEW_ACTION"
                      : section === "sources"
                        ? "CI_SOURCES_RECHECKS"
                        : "CI_REVIEW_HISTORY",
                  )}
                </Button>
              ))}
            </div>
            {expanded === doc.id && (
              <div className="space-y-3 border-t border-border bg-background/40 p-5 text-sm">
                <CurationPanel
                  id={doc.id}
                  contentType={doc.contentType}
                  view={view}
                />
                {view === "history" && (
                  <>
                    <p>
                      {t("CI_DOCUMENT_DATE")}: {t("CI_UNKNOWN")}
                    </p>
                    {doc.sourceUrl ? (
                      <a
                        className="block break-all underline"
                        href={doc.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("CI_SOURCE")}: {doc.sourceUrl}
                      </a>
                    ) : (
                      <p>{t("CI_NO_SOURCE")}</p>
                    )}
                    <p className="text-xs break-all text-muted-foreground">
                      SHA-256: {doc.sha256}
                    </p>
                  </>
                )}
              </div>
            )}
          </article>
        ))}
      </div>
      {documents.isSuccess && !!docs.length && !visibleDocs.length && (
        <p>{t("CI_NO_MATCHES")}</p>
      )}
      {documents.hasNextPage && (
        <Button
          variant="outline"
          disabled={documents.isFetchingNextPage}
          onClick={() => void documents.fetchNextPage()}
        >
          {t("CI_MORE")}
        </Button>
      )}
      <p className="text-xs text-muted-foreground">
        {t("CI_CURATOR_CAPTURE_NOTE")}
      </p>
    </div>
  )
}
